import React, { useState, KeyboardEvent } from 'react';
import { X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface EmailChip {
  email: string;
  warning?: string;
}

interface EmailChipInputProps {
  value: EmailChip[];
  onChange: (chips: EmailChip[]) => void;
  maxChips: number;
  ownEmail?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailChipInput: React.FC<EmailChipInputProps> = ({
  value,
  onChange,
  maxChips,
  ownEmail,
  label = 'Reviewer emails',
  placeholder = 'name@institution.edu',
  disabled = false,
  id = 'email-chip-input',
}) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addChip = (raw: string) => {
    const email = raw.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_RE.test(email) || email.length > 254) {
      setError('Please enter a valid email address.');
      return;
    }
    if (value.length >= maxChips) {
      setError(`You can invite at most ${maxChips} reviewers per paper.`);
      return;
    }
    if (value.some((c) => c.email === email)) {
      setError('That address is already on the list.');
      return;
    }
    let warning: string | undefined;
    if (ownEmail && email === ownEmail.toLowerCase()) {
      warning = 'You cannot invite yourself.';
    }
    onChange([...value, { email, warning }]);
    setDraft('');
    setError(null);
  };

  const removeChip = (email: string) => {
    onChange(value.filter((c) => c.email !== email));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === ' ' || e.key === 'Tab') {
      if (draft.trim()) {
        e.preventDefault();
        addChip(draft);
      }
    } else if (e.key === 'Backspace' && !draft && value.length) {
      removeChip(value[value.length - 1].email);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!text.includes(',') && !text.includes(';') && !/\s/.test(text)) return;
    e.preventDefault();
    const tokens = text
      .split(/[\s,;]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    let next = [...value];
    let firstError: string | null = null;
    for (const t of tokens) {
      const email = t.toLowerCase();
      if (next.length >= maxChips) {
        firstError = firstError ?? `You can invite at most ${maxChips} reviewers per paper.`;
        break;
      }
      if (!EMAIL_RE.test(email)) {
        firstError = firstError ?? `Skipped invalid address: ${t}`;
        continue;
      }
      if (next.some((c) => c.email === email)) continue;
      const warning =
        ownEmail && email === ownEmail.toLowerCase() ? 'You cannot invite yourself.' : undefined;
      next.push({ email, warning });
    }
    onChange(next);
    setDraft('');
    setError(firstError);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div
        className={cn(
          'min-h-[42px] w-full rounded-md border border-input bg-background px-2 py-1.5 flex flex-wrap gap-1.5 items-center',
          disabled && 'opacity-60',
        )}
      >
        {value.map((chip) => (
          <Badge
            key={chip.email}
            variant={chip.warning ? 'destructive' : 'secondary'}
            className="pl-2 pr-1 py-1 gap-1 max-w-full"
          >
            {chip.warning ? (
              <AlertCircle className="h-3 w-3 shrink-0" aria-hidden />
            ) : (
              <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden />
            )}
            <span className="text-xs truncate" title={chip.warning ?? chip.email}>
              {chip.email}
            </span>
            <button
              type="button"
              onClick={() => removeChip(chip.email)}
              disabled={disabled}
              aria-label={`Remove ${chip.email}`}
              className="rounded-full hover:bg-background/50 p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <Input
          id={id}
          type="email"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => {
            if (draft.trim()) addChip(draft);
          }}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled || value.length >= maxChips}
          className="flex-1 min-w-[160px] border-0 px-1 h-7 shadow-none focus-visible:ring-0"
        />
      </div>
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" aria-hidden />
          {error}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Press Enter, comma, or space to add. Up to {maxChips} per paper.
      </p>
    </div>
  );
};

export default EmailChipInput;
